/**
 * Hides the floating "الدفع السريع" / fast checkout button that Cartsaver CoD pins to the bottom of the
 * screen, on every device, at the merchant's request. Only that floating button is hidden: the app embed
 * stays on, so its cash-on-delivery form, pre-checkout OTP verification and campaign tracking keep working.
 * The button is found by its label text or an id/class naming the app; theme elements ([data-ksv-fixed],
 * dialogs, header, product forms) and any container holding a form or popup are never touched.
 */
const LABELS = /الدفع السريع|fast\s*checkout|quick\s*checkout/i;
const APP_HINT = /cartsaver|csv-|cod-?form|cod-?btn|cod-?button/i;
const moved = new WeakSet();

function fixedAncestor(node) {
  let el = node instanceof Element ? node : node.parentElement;
  while (el && el !== document.body) {
    if (el.closest('[data-ksv-fixed], dialog, header, s-product-form')) return null;
    const style = getComputedStyle(el);
    if (style.position === 'fixed') return el;
    el = el.parentElement;
  }
  return null;
}

function isBottomPinned(el) {
  const rect = el.getBoundingClientRect();
  return rect.height > 0 && rect.height < 160 && rect.bottom > window.innerHeight - 140;
}

function hide(el) {
  if (moved.has(el)) return;
  // Never hide a container that also holds the app's order form or popup, only the floating button.
  if (el.querySelector('form, input, select, textarea, [role="dialog"]')) return;
  moved.add(el);
  el.style.setProperty('display', 'none', 'important');
  el.dataset.ksvHidden = 'true';
}

const HINT_SELECTOR = '[id*="cartsaver" i], [class*="cartsaver" i], [id*="csv-" i], [class*="cod-" i]';

function collect(root, candidates) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (LABELS.test(n.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  for (let n = walker.nextNode(); n; n = walker.nextNode()) candidates.add(n);
  const hinted = root.matches?.(HINT_SELECTOR) ? [root] : [];
  hinted.concat([...root.querySelectorAll(HINT_SELECTOR)]).forEach((el) => {
    if (APP_HINT.test(`${el.id} ${el.className}`)) candidates.add(el);
  });
}

// Scans only the given roots (the nodes that were just added), not the whole page,
// so the cart drawer, sliders and search updates don't trigger a full-page walk each time.
function scan(roots = [document.body]) {
  const candidates = new Set();
  for (const root of roots) {
    if (root.isConnected) collect(root, candidates);
  }
  for (const node of candidates) {
    const el = fixedAncestor(node);
    if (el && isBottomPinned(el)) hide(el);
  }
}

let timer;
let pending = new Set();
let fullScan = false;
const flush = () => {
  const roots = fullScan ? [document.body] : [...pending];
  pending = new Set();
  fullScan = false;
  if (roots.length) scan(roots);
};
const schedule = (full = false) => {
  if (full) fullScan = true;
  clearTimeout(timer);
  timer = setTimeout(flush, 150);
};
scan();
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) pending.add(node);
      else if (node.nodeType === Node.TEXT_NODE && node.parentElement) pending.add(node.parentElement);
    }
  }
  if (pending.size) schedule();
}).observe(document.body, { childList: true, subtree: true });
// One full pass after load (and a late one) catches a button that was styled fixed after it was inserted.
window.addEventListener('load', () => {
  schedule(true);
  setTimeout(() => schedule(true), 3000);
});
